import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  CheckIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  CubeIcon,
} from '@heroicons/react/24/outline'
import { consignmentService } from '../../services/consignmentService'
import { customerService } from '../../services/customerService'
import { productService } from '../../services/productService'
import { serialService } from '../../services/serialService'
import toast from 'react-hot-toast'

interface ConsignmentItem {
  id: string
  product_id: number
  product_name: string
  product_sku?: string
  unit_price: number
  serials: Array<{ serial_id: number; serial_number: string }>
}

export const CreateConsignmentPage = () => {
  const navigate = useNavigate()

  const [customerId, setCustomerId] = useState('')
  const [issueDate, setIssueDate] = useState(() => new Date().toISOString().split('T')[0])
  const [expectedReturnDate, setExpectedReturnDate] = useState('')
  const [notes, setNotes] = useState('')
  const [items, setItems] = useState<ConsignmentItem[]>([])

  // Current item being built
  const [currentProductId, setCurrentProductId] = useState<number | null>(null)
  const [currentProductName, setCurrentProductName] = useState('')
  const [currentUnitPrice, setCurrentUnitPrice] = useState(0)
  const [selectedSerialIds, setSelectedSerialIds] = useState<Set<number>>(new Set())

  // Customer dropdown
  const [customerSearch, setCustomerSearch] = useState('')
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false)
  const customerDropdownRef = useRef<HTMLDivElement>(null)

  // Product dropdown
  const [productSearch, setProductSearch] = useState('')
  const [showProductDropdown, setShowProductDropdown] = useState(false)
  const productDropdownRef = useRef<HTMLDivElement>(null)

  const { data: customersData } = useQuery({
    queryKey: ['customers', customerSearch],
    queryFn: () => customerService.getAll({ search: customerSearch, limit: 50 }),
  })

  const customers: any[] = customersData?.data?.data?.items || []
  const selectedCustomer = customers.find((c: any) => c.customer_id.toString() === customerId)

  const { data: productsData } = useQuery({
    queryKey: ['products', productSearch],
    queryFn: () => productService.search(productSearch),
    enabled: productSearch.length >= 2,
  })

  const products: any[] = productsData?.data?.data || []

  const { data: serialsData } = useQuery({
    queryKey: ['available-serials', currentProductId],
    queryFn: () => serialService.getAvailable(currentProductId!),
    enabled: !!currentProductId,
  })

  const availableSerials: any[] = serialsData?.data?.data || []

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target as Node)) {
        setShowCustomerDropdown(false)
      }
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target as Node)) {
        setShowProductDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const createMutation = useMutation({
    mutationFn: (data: any) => consignmentService.create(data),
    onSuccess: (response) => {
      toast.success('Consignment created successfully')
      navigate(`/consignments/${response.data.data.consignment_id}`)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create consignment')
    },
  })

  const toggleSerial = (serialId: number) => {
    setSelectedSerialIds((prev) => {
      const next = new Set(prev)
      if (next.has(serialId)) {
        next.delete(serialId)
      } else {
        next.add(serialId)
      }
      return next
    })
  }

  const addItem = () => {
    if (!currentProductId) {
      toast.error('Please select a product')
      return
    }
    if (selectedSerialIds.size === 0) {
      toast.error('Please select at least one serial number')
      return
    }

    const selectedSerials = availableSerials
      .filter((s) => selectedSerialIds.has(s.serial_id))
      .map((s) => ({ serial_id: s.serial_id, serial_number: s.serial_number }))

    // Don't allow adding same product twice; merge serials instead
    const existingIndex = items.findIndex((i) => i.product_id === currentProductId)
    if (existingIndex >= 0) {
      const existing = items[existingIndex]
      const existingIds = new Set(existing.serials.map((s) => s.serial_id))
      const newSerials = selectedSerials.filter((s) => !existingIds.has(s.serial_id))
      if (newSerials.length === 0) {
        toast.error('These serials are already added')
        return
      }
      const updated = [...items]
      updated[existingIndex] = {
        ...existing,
        serials: [...existing.serials, ...newSerials],
        unit_price: currentUnitPrice || existing.unit_price,
      }
      setItems(updated)
    } else {
      setItems((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          product_id: currentProductId,
          product_name: currentProductName,
          product_sku: products.find((p) => p.product_id === currentProductId)?.sku,
          unit_price: currentUnitPrice,
          serials: selectedSerials,
        },
      ])
    }

    // Reset current item
    setCurrentProductId(null)
    setCurrentProductName('')
    setCurrentUnitPrice(0)
    setSelectedSerialIds(new Set())
    setProductSearch('')
  }

  const removeItem = (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!customerId) {
      toast.error('Please select a customer')
      return
    }
    if (items.length === 0) {
      toast.error('Please add at least one item')
      return
    }

    createMutation.mutate({
      customer_id: Number(customerId),
      issue_date: issueDate,
      expected_return_date: expectedReturnDate || undefined,
      notes: notes || undefined,
      items: items.map((item) => ({
        product_id: item.product_id,
        unit_price: item.unit_price,
        serials: item.serials.map((s) => s.serial_id),
      })),
    })
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/consignments')} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">New Consignment</h1>
          <p className="text-sm text-gray-500">Issue stock to a customer on consignment</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer & Dates */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Consignment Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Customer */}
              <div className="relative" ref={customerDropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer *</label>
                <button
                  type="button"
                  onClick={() => setShowCustomerDropdown(!showCustomerDropdown)}
                  className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white text-left"
                >
                  <span className={selectedCustomer ? 'text-gray-900' : 'text-gray-500'}>
                    {selectedCustomer
                      ? `${selectedCustomer.company_name || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`}`
                      : 'Select Customer'}
                  </span>
                  <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${showCustomerDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showCustomerDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-auto">
                    <div className="p-2 border-b sticky top-0 bg-white">
                      <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search customers..."
                          value={customerSearch}
                          onChange={(e) => setCustomerSearch(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="py-1">
                      {customers.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">No customers found</div>
                      ) : (
                        customers.map((c: any) => (
                          <button
                            key={c.customer_id}
                            type="button"
                            onClick={() => {
                              setCustomerId(c.customer_id.toString())
                              setShowCustomerDropdown(false)
                              setCustomerSearch('')
                            }}
                            className={`w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center justify-between ${customerId === c.customer_id.toString() ? 'bg-primary-50' : ''}`}
                          >
                            <div>
                              <p className="font-medium text-sm">{c.company_name || `${c.first_name} ${c.last_name}`}</p>
                              <p className="text-xs text-gray-500">{c.customer_code || c.phone || c.email}</p>
                            </div>
                            {customerId === c.customer_id.toString() && <CheckIcon className="w-4 h-4 text-primary-600" />}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Issue Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date *</label>
                <input
                  type="date"
                  value={issueDate}
                  onChange={(e) => setIssueDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>

              {/* Expected Return Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expected Return Date</label>
                <input
                  type="date"
                  value={expectedReturnDate}
                  onChange={(e) => setExpectedReturnDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Any special terms or conditions..."
              />
            </div>
          </div>

          {/* Add Items */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Items</h2>
            <div className="space-y-4">
              {/* Product Selector */}
              <div className="relative" ref={productDropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product *</label>
                <div className="relative">
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => {
                      setProductSearch(e.target.value)
                      setShowProductDropdown(true)
                      if (e.target.value === '') {
                        setCurrentProductId(null)
                        setCurrentProductName('')
                        setSelectedSerialIds(new Set())
                      }
                    }}
                    onFocus={() => setShowProductDropdown(true)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 pr-9"
                    placeholder={currentProductName || 'Search products...'}
                  />
                  {currentProductId && <CheckIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />}
                </div>

                {showProductDropdown && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-auto">
                    {productSearch.length < 2 ? (
                      <div className="px-4 py-3 text-sm text-gray-500 text-center">Type at least 2 characters to search</div>
                    ) : products.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500 text-center">No products found</div>
                    ) : (
                      <div className="py-1">
                        {products.map((product: any) => (
                          <button
                            key={product.product_id}
                            type="button"
                            onClick={() => {
                              setCurrentProductId(product.product_id)
                              setCurrentProductName(product.product_name)
                              setProductSearch('')
                              setShowProductDropdown(false)
                              setSelectedSerialIds(new Set())
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center justify-between"
                          >
                            <div>
                              <p className="font-medium text-sm">{product.product_name}</p>
                              <p className="text-xs text-gray-500">{product.sku && `SKU: ${product.sku}`}{product.category_name && ` • ${product.category_name}`}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {currentProductName && !showProductDropdown && (
                  <p className="mt-1 text-xs text-gray-500">Selected: {currentProductName}</p>
                )}
              </div>

              {/* Unit Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price (KSh)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={currentUnitPrice}
                  onChange={(e) => setCurrentUnitPrice(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="0.00"
                />
              </div>

              {/* Available Serials */}
              {currentProductId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Available Serial Numbers
                    <span className="ml-2 text-xs text-gray-500 font-normal">({availableSerials.length} available)</span>
                  </label>
                  {availableSerials.length === 0 ? (
                    <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-500 text-center">
                      No available serial numbers for this product
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg max-h-60 overflow-auto">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3">
                        {availableSerials.map((serial: any) => (
                          <label
                            key={serial.serial_id}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                              selectedSerialIds.has(serial.serial_id)
                                ? 'bg-primary-50 border-primary-500 text-primary-700'
                                : 'bg-white border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                              checked={selectedSerialIds.has(serial.serial_id)}
                              onChange={() => toggleSerial(serial.serial_id)}
                            />
                            <span className="font-mono text-xs truncate">{serial.serial_number}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {selectedSerialIds.size} serial{selectedSerialIds.size !== 1 ? 's' : ''} selected
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={addItem}
                className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-500 hover:text-primary-600"
              >
                <PlusIcon className="w-5 h-5 inline mr-2" />
                Add Item to Consignment
              </button>
            </div>
          </div>

          {/* Items List */}
          {items.length > 0 && (
            <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">Items ({items.length})</h3>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Serials</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                            <CubeIcon className="w-5 h-5 text-gray-400" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{item.product_name}</p>
                            {item.product_sku && <p className="text-xs text-gray-500">{item.product_sku}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className="inline-flex items-center px-2 py-1 bg-gray-100 rounded text-sm font-medium">
                          {item.serials.length}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        KSh {item.unit_price.toLocaleString()}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Summary */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Summary</h2>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Customer</span>
                <span className="font-medium">{selectedCustomer ? selectedCustomer.company_name || `${selectedCustomer.first_name} ${selectedCustomer.last_name}` : '—'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Products</span>
                <span className="font-medium">{items.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total Serials</span>
                <span className="font-medium">{items.reduce((sum, i) => sum + i.serials.length, 0)}</span>
              </div>
              <div className="border-t pt-3">
                <div className="flex justify-between font-bold text-lg">
                  <span>Total Value</span>
                  <span>KSh {items.reduce((sum, i) => sum + i.unit_price * i.serials.length, 0).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/consignments')}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Consignment'}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
